import { defineCollection } from "@rebasepro/common";
/**
 * Signals found by the nightly sourcing agent. The kanban board is the review
 * queue: agent inserts into "new"; a human moves cards through the pipeline
 * and is the ONLY one who ever contacts anybody.
 *
 * Displayed as "Signals", not "Leads". In sales vocabulary a lead and a
 * prospect are the same word, so a board of each taught nobody which was
 * which. What this table actually holds is *one thing a person said in
 * public* — one post, one row. The person who said it lives in People, and one
 * person can produce several signals.
 *
 * The slug and table stay `leads`: renaming them creates a new empty table and
 * strands whatever the nightly cron has already written, while the display
 * name is the part anyone actually reads.
 */
/** A value as a trimmed string, or "" — display roles are text. */
const str = (value) => (typeof value === "string" ? value.trim() : value == null ? "" : String(value));
/**
 * Short labels for the row chips.
 *
 * Not the enum's own labels: "GitHub Discussions" and "Stack Overflow" are
 * accurate and too long to sit in a row beside two other chips. The enum keeps
 * the full names for the form, where there is room for them.
 */
const SOURCE_LABELS = {
    hn: "HN",
    reddit: "Reddit",
    github: "GitHub",
    github_discussions: "GH Discussions",
    stackoverflow: "SO",
    devto: "dev.to",
    mastodon: "Mastodon",
    lobsters: "Lobsters",
    other: "Other"
};
const SEGMENT_LABELS = {
    product_team: "Product team",
    agency: "Agency",
    internal_tools: "Internal tools",
    sql_migrator: "SQL migration"
};
const leadsCollection = defineCollection({
    name: "Signals",
    singularName: "Signal",
    slug: "leads",
    table: "leads",
    description: "Something someone said in public that suggests they need rebase. One post = one signal. Approve one to pull the person behind it into People.",
    history: true,
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        title: {
            name: "Title",
            type: "string",
            validation: { required: true },
            description: "Title of the post/thread where the pain was voiced"
        },
        url: {
            name: "URL",
            type: "string",
            validation: { required: true, unique: true },
            // A statement about the data, not the panel: it becomes
            // `format: "uri"` in the generated OpenAPI contract.
            url: true
        },
        source: {
            name: "Source",
            type: "string",
            validation: { required: true },
            enum: [
                { id: "hn", label: "Hacker News", color: "orange" },
                { id: "reddit", label: "Reddit", color: "red" },
                { id: "github", label: "GitHub", color: "gray" },
                { id: "github_discussions", label: "GitHub Discussions", color: "grayDark" },
                { id: "stackoverflow", label: "Stack Overflow", color: "yellow" },
                { id: "devto", label: "dev.to", color: "purple" },
                { id: "mastodon", label: "Mastodon", color: "blue" },
                { id: "lobsters", label: "Lobste.rs", color: "redLight" },
                { id: "other", label: "Other", color: "blueLight" }
            ]
        },
        author: {
            name: "Author",
            type: "string",
            description: "Public handle of the person voicing the pain"
        },
        quote: {
            name: "Quote",
            type: "string",
            admin: { markdown: true },
            description: "Short verbatim quote of the pain from the post"
        },
        pain: {
            name: "Pain",
            type: "string",
            description: "One-line summary of the problem they have"
        },
        segment: {
            name: "Segment",
            type: "string",
            enum: [
                { id: "product_team", label: "Product team", color: "blue" },
                { id: "agency", label: "Agency / freelancer", color: "purple" },
                { id: "internal_tools", label: "Internal tools", color: "teal" },
                { id: "sql_migrator", label: "SQL migrator", color: "yellow" }
            ]
        },
        score: {
            name: "Score",
            type: "number",
            description: "Fit score 1-5 from the qualification model (only ≥3 is stored)",
            validation: { min: 1, max: 5 }
        },
        angle: {
            name: "Angle",
            type: "string",
            enum: [
                { id: "A", label: "A — RLS done right, admin included", color: "blue" },
                { id: "B", label: "B — Client-ready admin panel", color: "purple" },
                { id: "C", label: "C — Retool bill → zero", color: "green" }
            ]
        },
        draft_reply: {
            name: "Draft reply",
            type: "string",
            admin: { markdown: true },
            description: "Suggested public reply — edit before posting, never auto-sent"
        },
        posted_at: {
            name: "Posted at",
            type: "date",
            description: "When the original post was published"
        },
        decided_by: {
            name: "Moved by",
            type: "string",
            description: "Who last moved this card out of New — so an agent decision is never mistaken for yours.",
            defaultValue: "human",
            enum: [
                { id: "human", label: "You", color: "green" },
                { id: "agent", label: "Agent", color: "blue" }
            ],
            admin: { readOnly: true }
        },
        decided_reason: {
            name: "Why it moved",
            type: "string",
            description: "The rule that fired, when the agent moved it. Empty when a human did.",
            admin: { readOnly: true }
        },
        homepage: {
            name: "Their site",
            type: "string",
            url: true,
            description: "A site linked from the post — the bridge to Companies, where its stack gets checked"
        },
        scored_by: {
            name: "Scored by",
            type: "string",
            defaultValue: "heuristic",
            description: "Which judge produced the score. A keyword score is real but blunt; it carries no drafted reply.",
            enum: [
                { id: "model", label: "Model", color: "green" },
                { id: "heuristic", label: "Keywords only", color: "orange" }
            ]
        },
        score_reason: {
            name: "Why this score",
            type: "string",
            admin: { markdown: true },
            description: "What matched, and what argued against it"
        },
        status: {
            name: "Status",
            type: "string",
            validation: { required: true },
            defaultValue: "new",
            enum: [
                { id: "new", label: "New", color: "blue" },
                { id: "approved", label: "Approved", color: "purple" },
                // Enrichment consumes `approved` and leaves this behind. Without
                // a terminal state the same lead is re-resolved over the network
                // every night for the life of the board — and nothing else in
                // this app ever wrote a lead's status, so `approved` was
                // permanent by accident.
                { id: "enriched", label: "Person found", color: "cyanDark" },
                { id: "contacted", label: "Contacted", color: "green" },
                { id: "replied", label: "They replied", color: "teal" },
                { id: "skipped", label: "Skipped", color: "gray" }
            ]
        },
        found_at: {
            name: "Found at",
            type: "date",
            autoValue: "on_create",
            admin: { readOnly: true }
        },
        __order: {
            name: "Order",
            type: "string",
            admin: { disabled: true, hideFromCollection: true }
        }
    },
    admin: {
        icon: "Radio",
        group: "Outreach",
        defaultViewMode: "list",
        enabledViews: ["list", "table", "kanban"],
        kanban: {
            columnProperty: "status"
        },
        orderProperty: "__order",
        /**
         * What a row has to say before anyone opens it.
         *
         * A list of titles and timestamps is a list of things to click on. The
         * three facts that actually decide whether a signal is worth reading —
         * where it came from, how the model scored it, and what the person was
         * actually complaining about — were all one click away, so triaging
         * thirty-four of them meant thirty-four round trips.
         *
         * `posted_at` rather than `found_at` for the date, because a lead is a
         * moment: what matters is how long ago THEY wrote, not when we noticed.
         * The board retires anything whose thread is older than two weeks for
         * the same reason.
         */
        display: {
            title: "title",
            // What they said, in their words. `pain` is the model's one-line
            // reading of it and `quote` is the sentence it read — the reading
            // first, the evidence when there is no reading.
            subtitle: ({ entity }) => str(entity.values.pain) || str(entity.values.quote) || str(entity.values.score_reason),
            status: "status",
            date: "posted_at",
            // Source first: a Reddit thread and a GitHub issue are different
            // kinds of work, and it is the fastest thing to read on a row.
            tags: ({ entity }) => [
                SOURCE_LABELS[str(entity.values.source)] ?? str(entity.values.source),
                entity.values.score ? `score ${entity.values.score}` : "",
                SEGMENT_LABELS[str(entity.values.segment)] ?? "",
                entity.values.scored_by === "heuristic" ? "keywords only" : ""
            ].filter(Boolean)
        },
        sort: ["found_at", "desc"],
        propertiesOrder: [
            "title",
            "status",
            "decided_by",
            "score",
            "segment",
            "angle",
            "source",
            "url",
            "author",
            "homepage",
            "scored_by",
            "score_reason",
            "pain",
            "quote",
            "draft_reply",
            "posted_at",
            "found_at",
            "__order"
        ],
        filterPresets: [
            {
                label: "Hot (score 5)",
                filterValues: { score: ["==", 5] }
            },
            {
                label: "Undecided",
                filterValues: { status: ["==", "new"] }
            },
            {
                label: "Agencies",
                filterValues: { segment: ["==", "agency"] }
            },
            {
                label: "Needs a reply written",
                filterValues: { scored_by: ["==", "heuristic"] }
            }
        ]
    }
});
export default leadsCollection;
